import {
  BUILDUP_FUNCTIONS,
  NORMALIZER_TYPES,
  WASHOFF_FUNCTIONS,
  upsertRow,
  type BuildupRow,
  type WashoffRow,
} from "../model/inp/buildupWashoff.ts";
import { nextAvailableLandUseName, type LandUse } from "../model/inp/landuses.ts";
import type { Pollutant } from "../model/inp/pollutants.ts";
import { useT } from "../i18n/hooks.ts";
import { optionLabelKey } from "../i18n/fieldLabels.ts";
import { NumberInput } from "./fields.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";

const BUILDUP_DEFAULTS: Omit<BuildupRow, "landUse" | "pollutant"> = {
  function: "NONE",
  coeff1: 0,
  coeff2: 0,
  coeff3: 0,
  normalizer: "AREA",
};
const WASHOFF_DEFAULTS: Omit<WashoffRow, "landUse" | "pollutant"> = {
  function: "NONE",
  coeff1: 0,
  coeff2: 0,
  cleaningEfficiency: 0,
  bmpEfficiency: 0,
};

/**
 * Buildup/washoff rows are created lazily per pollutant (via upsertRow)
 * rather than pre-materializing a full LandUse x Pollutant matrix, so a
 * pair the user never touches never gets a row — matching real files, which
 * only list pairs that actually have something configured.
 */
export function LandUsesEditor({
  landUses,
  onChange,
  selectedName,
  onSelect,
  pollutants,
  buildup,
  onChangeBuildup,
  washoff,
  onChangeWashoff,
}: {
  landUses: LandUse[];
  onChange: (landUses: LandUse[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
  pollutants: Pollutant[];
  buildup: BuildupRow[];
  onChangeBuildup: (rows: BuildupRow[]) => void;
  washoff: WashoffRow[];
  onChangeWashoff: (rows: WashoffRow[]) => void;
}) {
  const t = useT();
  const { selected, add, remove, update } = useNamedCollection(
    landUses,
    onChange,
    selectedName,
    onSelect
  );

  function addLandUse() {
    const name = nextAvailableLandUseName(new Set(landUses.map((lu) => lu.name)));
    add({ name, sweepingInterval: 0, sweepingAvailability: 0, lastSweptDay: 0 });
  }

  /** Also drops this land use's own buildup/washoff rows so they don't end up orphaned. */
  function deleteLandUse(name: string) {
    remove(name);
    onChangeBuildup(buildup.filter((r) => r.landUse !== name));
    onChangeWashoff(washoff.filter((r) => r.landUse !== name));
  }

  /** Kept local (not useNamedCollection's shared rename) so the new name can also be carried over to this land use's own buildup/washoff rows. */
  function renameSelected(name: string) {
    if (!selected) return;
    const trimmed = name.trim();
    if (!trimmed || (trimmed !== selected.name && landUses.some((lu) => lu.name === trimmed)))
      return;
    const oldName = selected.name;
    onChange(landUses.map((lu) => (lu.name === oldName ? { ...lu, name: trimmed } : lu)));
    onChangeBuildup(buildup.map((r) => (r.landUse === oldName ? { ...r, landUse: trimmed } : r)));
    onChangeWashoff(washoff.map((r) => (r.landUse === oldName ? { ...r, landUse: trimmed } : r)));
    onSelect(trimmed);
  }

  function set<K extends keyof LandUse>(key: K, value: LandUse[K]) {
    update((lu) => ({ ...lu, [key]: value }));
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={landUses}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addLandUse}
        addLabel={t("projectData.addLandUse")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("projectData.noLandUseSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deleteLandUse(selected.name)}
              deleteLabel={t("projectData.deleteLandUse")}
            />
            <div className="named-collection-fields">
              <label className="options-field">
                {t("projectData.sweepingInterval")}
                <NumberInput
                  value={selected.sweepingInterval}
                  onChange={(v) => set("sweepingInterval", v)}
                />
              </label>
              <label className="options-field">
                {t("projectData.sweepingAvailability")}
                <NumberInput
                  value={selected.sweepingAvailability}
                  onChange={(v) => set("sweepingAvailability", v)}
                />
              </label>
              <label className="options-field">
                {t("projectData.lastSweptDay")}
                <NumberInput
                  value={selected.lastSweptDay}
                  onChange={(v) => set("lastSweptDay", v)}
                />
              </label>
            </div>

            {pollutants.length === 0 ? (
              <p className="property-panel-hint">{t("projectData.noPollutantsYet")}</p>
            ) : (
              <>
                <h4 className="named-collection-subsection-title">{t("projectData.tabBuildup")}</h4>
                <div className="named-collection-points-table-wrap">
                  <table className="named-collection-points-table">
                    <thead>
                      <tr>
                        <th>{t("projectData.pollutant")}</th>
                        <th>{t("projectData.buildupFunction")}</th>
                        <th>{t("projectData.coeff1")}</th>
                        <th>{t("projectData.coeff2")}</th>
                        <th>{t("projectData.coeff3")}</th>
                        <th>{t("projectData.normalizer")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pollutants.map((pollutant) => {
                        const row = buildup.find(
                          (r) => r.landUse === selected.name && r.pollutant === pollutant.name
                        );
                        function patchBuildup(patch: Partial<BuildupRow>) {
                          onChangeBuildup(
                            upsertRow(
                              buildup,
                              selected!.name,
                              pollutant.name,
                              {
                                landUse: selected!.name,
                                pollutant: pollutant.name,
                                ...BUILDUP_DEFAULTS,
                              },
                              patch
                            )
                          );
                        }
                        return (
                          <tr key={pollutant.name}>
                            <td className="pattern-table-label-cell">{pollutant.name}</td>
                            <td>
                              <select
                                value={row?.function ?? "NONE"}
                                onChange={(e) => patchBuildup({ function: e.target.value })}
                              >
                                {BUILDUP_FUNCTIONS.map((fn) => {
                                  const key = optionLabelKey(fn);
                                  return (
                                    <option key={fn} value={fn}>
                                      {key ? t(key) : fn}
                                    </option>
                                  );
                                })}
                              </select>
                            </td>
                            <td>
                              <NumberInput
                                value={row?.coeff1 ?? 0}
                                onChange={(v) => patchBuildup({ coeff1: v })}
                              />
                            </td>
                            <td>
                              <NumberInput
                                value={row?.coeff2 ?? 0}
                                onChange={(v) => patchBuildup({ coeff2: v })}
                              />
                            </td>
                            <td>
                              <NumberInput
                                value={row?.coeff3 ?? 0}
                                onChange={(v) => patchBuildup({ coeff3: v })}
                              />
                            </td>
                            <td>
                              <select
                                value={row?.normalizer ?? "AREA"}
                                onChange={(e) => patchBuildup({ normalizer: e.target.value })}
                              >
                                {NORMALIZER_TYPES.map((n) => {
                                  const key = optionLabelKey(n);
                                  return (
                                    <option key={n} value={n}>
                                      {key ? t(key) : n}
                                    </option>
                                  );
                                })}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <h4 className="named-collection-subsection-title">{t("projectData.tabWashoff")}</h4>
                <div className="named-collection-points-table-wrap">
                  <table className="named-collection-points-table">
                    <thead>
                      <tr>
                        <th>{t("projectData.pollutant")}</th>
                        <th>{t("projectData.washoffFunction")}</th>
                        <th>{t("projectData.coeff1")}</th>
                        <th>{t("projectData.coeff2")}</th>
                        <th>{t("projectData.cleaningEfficiency")}</th>
                        <th>{t("projectData.bmpEfficiency")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pollutants.map((pollutant) => {
                        const row = washoff.find(
                          (r) => r.landUse === selected.name && r.pollutant === pollutant.name
                        );
                        function patchWashoff(patch: Partial<WashoffRow>) {
                          onChangeWashoff(
                            upsertRow(
                              washoff,
                              selected!.name,
                              pollutant.name,
                              {
                                landUse: selected!.name,
                                pollutant: pollutant.name,
                                ...WASHOFF_DEFAULTS,
                              },
                              patch
                            )
                          );
                        }
                        return (
                          <tr key={pollutant.name}>
                            <td className="pattern-table-label-cell">{pollutant.name}</td>
                            <td>
                              <select
                                value={row?.function ?? "NONE"}
                                onChange={(e) => patchWashoff({ function: e.target.value })}
                              >
                                {WASHOFF_FUNCTIONS.map((fn) => {
                                  const key = optionLabelKey(fn);
                                  return (
                                    <option key={fn} value={fn}>
                                      {key ? t(key) : fn}
                                    </option>
                                  );
                                })}
                              </select>
                            </td>
                            <td>
                              <NumberInput
                                value={row?.coeff1 ?? 0}
                                onChange={(v) => patchWashoff({ coeff1: v })}
                              />
                            </td>
                            <td>
                              <NumberInput
                                value={row?.coeff2 ?? 0}
                                onChange={(v) => patchWashoff({ coeff2: v })}
                              />
                            </td>
                            <td>
                              <NumberInput
                                value={row?.cleaningEfficiency ?? 0}
                                onChange={(v) => patchWashoff({ cleaningEfficiency: v })}
                              />
                            </td>
                            <td>
                              <NumberInput
                                value={row?.bmpEfficiency ?? 0}
                                onChange={(v) => patchWashoff({ bmpEfficiency: v })}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
